import { env, useAppSelector, useWebCollaborationLogic, webCollaborationMessages } from 'azshared';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Draggable from 'react-draggable';
import { useIntl } from 'react-intl';
import { useGlobalActions } from '../../hooks/global-actions';
import useElementSize from '../../hooks/use-element-size';
import { useInterval } from '../../hooks/use-interval';
import { AppButtonIcon } from '../button-icon/app-button-icon';
import { AppDialog } from '../dialog/app-dialog';
import { WebCollaborationPermissionRequestsDialog } from '../web-collaboration-permission-requests-dialog/web-collaboration-permission-requests-dialog';
import { WebCollaborationSharingBlockDialog } from '../web-collaboration-sharing-block-dialog/web-collaboration-sharing-block-dialog';
import './web-collaboration-overlay.scss';

const mediaConstraints: MediaStreamConstraints = {
  video: true,
  preferCurrentTab: true,
  audio: {
    echoCancellation: true,
    noiseSuppression: true
  }
};

export const WebCollaborationOverlay = () => {
  const { formatMessage } = useIntl();
  const globalActions = useGlobalActions();
  const channel = useRef<RTCDataChannel | null>(null);
  const { status, createAnswer, offer, roomId, closeCall, resetOffer, screenSharingActive, setScreenSharingActive } = useWebCollaborationLogic(globalActions);
  const prospectVideo = useRef<HTMLVideoElement | null>(null);
  const cfVideo = useRef<HTMLVideoElement | null>(null);
  const prospectDraggableRef = useRef<HTMLDivElement>(null);
  const cfDraggableRef = useRef<HTMLDivElement>(null);
  const peerRef = useRef<RTCPeerConnection | null>(null);
  const prospectStreamRef = useRef<MediaStream | undefined>(undefined);
  const [audioActive, setAudioActive] = useState<boolean>(true);
  const [prospectVideoActive, setProspectVideoActive] = useState<boolean>(true);
  const screenTrackRef = useRef<MediaStreamTrack | undefined>(undefined);
  const [localOffer, setLocalOffer] = useState<string>();
  const [openReconnectingDialog, setOpenReconnectingDialog] = useState(false);
  const updateQuestionnairesStatus = useAppSelector((state) => state.features.mifidSurvey?.updateQuestionnairesStatusStatus);
  const [openPermissionRequestsDialog, setOpenPermissionRequestsDialog] = useState(false);
  const [buttonsRef, buttonsSize] = useElementSize();
  const [, setCfVideoActive] = useState<boolean>(true);
  const [isPlaying, setPlaying] = useState(false);
  const [connectionState, setConnectionState] = useState<RTCPeerConnectionState | undefined>(undefined);
  const [isCallClosedByCf, setIsCallClosedByCf] = useState(false);
  const lastMessageAt = useRef<number>(Date.now());
  const lastIceCandiDateTime = useRef<number | undefined>(undefined);
  const answerSended = useRef(false);

  const onStopSharedScreen = useCallback(() => {
    screenTrackRef.current?.stop();
    setScreenSharingActive(false);
    screenTrackRef.current = undefined;
    peerRef.current?.getSenders()?.find((s: any) => s.track?.kind === 'video')?.replaceTrack(prospectStreamRef.current?.getTracks()[1] || null);
    if (channel.current?.readyState === 'open') {
      try {
        channel.current.send(JSON.stringify({ screenSharingActive: false }));
      } catch (e) {
        console.error('Error stopping screen sharing', e);
      }
    }
  }, [setScreenSharingActive]);

  const closePeerConnection = useCallback(() => {
    channel.current?.close();
    peerRef.current?.close();
    peerRef.current = null;
    answerSended.current = false;
    lastIceCandiDateTime.current = undefined;
    setPlaying(false);
    setConnectionState('closed');
  }, []);

  const stopStream = useCallback(async () => {
    onStopSharedScreen();
    prospectStreamRef.current?.getTracks()?.forEach(t => {
      t.stop();
      t.enabled = false;
    });
    closePeerConnection();
    prospectStreamRef.current = undefined;
  }, [onStopSharedScreen, closePeerConnection]);

  const handleCloseCall = useCallback(async () => {
    await stopStream();
    closeCall();
  }, [closeCall, stopStream]);

  const handleMessageLogic = useRef<(data: any) => void>(undefined);
  handleMessageLogic.current = (data: any) => {
    if (data.type === 'ping') {
      setOpenReconnectingDialog(false);
      return;
    }
    if (data.type === 'close') {
      console.log('[Prospect] CF closed the call. Disconnecting peer but keeping webcam.');
      closePeerConnection();
      setIsCallClosedByCf(true);
      return;
    }
    setCfVideoActive(data.cfVideoActive);
  };

  const handleMessage = useCallback((event: MessageEvent) => {
    lastMessageAt.current = Date.now();
    try {
      const data = JSON.parse(event.data);
      handleMessageLogic.current?.(data);
    } catch (e) {
      console.error('Error parsing message', e);
    }
  }, []);

  const handleDataChannel = useCallback((event: RTCDataChannelEvent) => {
    const dc = event.channel;
    channel.current = dc;
    dc.onopen = () => {
      try {
        dc.send(JSON.stringify({ isMobile: false }));
      } catch (e) {
        console.error('Error sending initial data channel message', e);
      }
    };
    dc.onmessage = handleMessage;
  }, [handleMessage]);

  useEffect(() => {
    if (updateQuestionnairesStatus === 'succeeded') {
      if (channel.current?.readyState === 'open') {
        try {
          channel.current.send(JSON.stringify({ mifidStatus: 'completed' }));
        } catch (e) {
          console.error('Error sending mifid status', e);
        }
      }
      handleCloseCall();
    }
  }, [updateQuestionnairesStatus, handleCloseCall]);

  useEffect(() => { return () => { stopStream(); }; }, [stopStream]);

  useInterval(() => {
    const now = Date.now();
    if (!answerSended.current && lastIceCandiDateTime.current !== undefined && now - lastIceCandiDateTime.current > 1000) {
      if (peerRef.current?.localDescription) {
        createAnswer(JSON.stringify(peerRef.current.localDescription));
        setPlaying(false);
        lastIceCandiDateTime.current = undefined;
        answerSended.current = true;
      }
    }
  }, isPlaying ? 500 : null);

  const handleICECandidateEvent = useCallback((e: RTCPeerConnectionIceEvent) => {
    if (e.candidate === null && !answerSended.current) {
      if (peerRef.current?.localDescription) {
        createAnswer(JSON.stringify(peerRef.current.localDescription));
        setPlaying(false);
        lastIceCandiDateTime.current = undefined;
        answerSended.current = true;
      }
    }
  }, [createAnswer]);

  const handleTrackEvent = useCallback((e: RTCTrackEvent) => {
    if (cfVideo.current) cfVideo.current.srcObject = e.streams[0];
  }, []);

  const handleConnectionState = useCallback(() => {
    const state = peerRef.current?.connectionState;
    console.log('[Prospect] Peer connection state change:', state);
    if (state) setConnectionState(state);
    if (state === 'connected') {
      setOpenReconnectingDialog(false);
      setIsCallClosedByCf(false);
    } else if (state === 'failed' || state === 'closed' || state === 'disconnected') {
      if (!isCallClosedByCf) {
        console.log('[Prospect] Connection lost or closed, resetting state');
        setOpenReconnectingDialog(true);
        setPlaying(false);
        onStopSharedScreen();
        setLocalOffer(undefined);
        resetOffer();
      }
    }
  }, [onStopSharedScreen, resetOffer, isCallClosedByCf]);

  const createPeer = useCallback(() => {
    console.log('[Prospect] Creating new RTCPeerConnection');
    const peer = new RTCPeerConnection({ iceServers: [{ urls: env.iceServers }] });
    peerRef.current = peer;
    peer.onicecandidate = handleICECandidateEvent;
    peer.ontrack = handleTrackEvent;
    peer.onconnectionstatechange = handleConnectionState;
    peer.ondatachannel = handleDataChannel;
    return peer;
  }, [handleICECandidateEvent, handleTrackEvent, handleConnectionState, handleDataChannel]);

  const handleReceiveCall = useCallback(async (incoming: any) => {
    console.log('[Prospect] Processing handleReceiveCall (Offer received)');
    try {
      setIsCallClosedByCf(false);
      setOpenReconnectingDialog(false);
      const isStreamActive = prospectStreamRef.current?.getTracks().some(t => t.readyState === 'live');
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const webcamStream = isStreamActive ? prospectStreamRef.current! : await navigator.mediaDevices.getUserMedia(mediaConstraints);
      if (prospectVideo.current) prospectVideo.current.srcObject = webcamStream;
      setProspectVideoActive(true);
      setAudioActive(true);
      onStopSharedScreen();
      if (peerRef.current) {
        console.log('[Prospect] Closing existing peer connection before creating new one');
        peerRef.current.onconnectionstatechange = null;
        peerRef.current.onicecandidate = null;
        peerRef.current.ontrack = null;
        peerRef.current.ondatachannel = null;
        peerRef.current.close();
      }
      const peer = createPeer();
      await peer.setRemoteDescription(new RTCSessionDescription(incoming));
      webcamStream.getTracks().forEach(t => peer.addTrack(t, webcamStream));
      prospectStreamRef.current = webcamStream;
      const answer = await peer.createAnswer();
      await peer.setLocalDescription(answer);
      setPlaying(true);
      lastIceCandiDateTime.current = Date.now();
      answerSended.current = false;
      lastMessageAt.current = Date.now();
    } catch (err) {
      console.error('[Prospect] Error in handleReceiveCall:', err);
      setOpenPermissionRequestsDialog(true);
      setPlaying(false);
    }
  }, [createPeer, onStopSharedScreen]);

  useEffect(() => {
    if (status) {
      console.log('[Prospect] Room status poll update:', status, 'RoomId:', roomId);
    }
    if (offer && offer !== localOffer && !!status) {
      console.log('[Prospect] New/Changed offer string detected');
      handleReceiveCall(JSON.parse(offer));
      setLocalOffer(offer);
    }
  }, [offer, status, roomId, handleReceiveCall, localOffer]);

  useInterval(() => {
    if (status === 'CONNECTED') {
      // Increased threshold to 10 seconds to avoid false positives from browser throttling
      if (Date.now() - lastMessageAt.current > 10000) {
        if (!isCallClosedByCf) setOpenReconnectingDialog(true);
      }
    }
  }, 1000);

  const onShareScreen = useCallback(async () => {
    try {
      const sharingStream = await navigator.mediaDevices.getDisplayMedia({ ...mediaConstraints, audio: false});
      setScreenSharingActive(true);
      const st = sharingStream.getTracks()?.find(i => i.kind === 'video');
      if (st) {
        st.onended = onStopSharedScreen;
        peerRef.current?.getSenders()?.find(s => s.track?.kind === 'video')?.replaceTrack(st);
      }
      screenTrackRef.current = st;
      if (channel.current?.readyState === 'open') {
        try {
          channel.current.send(JSON.stringify({ screenSharingActive: true, windowSize: { width: window.innerWidth, height: window.innerHeight }, buttonsSize, buttonsTop: 30, screenWidth: window.screen.width, browserWidth: window.innerWidth, windowLeft: 0 }));
        } catch (e) {
          console.error('Error sending screen share data', e);
        }
      }
    } catch (e) {
      console.error('Error starting screen share', e);
    }
  }, [onStopSharedScreen, setScreenSharingActive, buttonsSize]);

  const onToggleVideo = useCallback(async () => {
    if (prospectStreamRef.current?.getVideoTracks()[0]) {
      if (prospectStreamRef.current.getVideoTracks()[0].readyState === 'live') {
        prospectStreamRef.current.getVideoTracks()[0].stop();
        setProspectVideoActive(false);
      } else {
        try {
          const stream = await navigator.mediaDevices.getUserMedia({ ...mediaConstraints, audio: false });
          prospectStreamRef.current.removeTrack(prospectStreamRef.current.getVideoTracks()[0]);
          prospectStreamRef.current.addTrack(stream.getVideoTracks()[0]);
          setProspectVideoActive(true);
        } catch (e) {
          console.error('Error toggling video', e);
        }
      }
    }
    if (!screenSharingActive) peerRef.current?.getSenders()?.find(s => s.track?.kind === 'video')?.replaceTrack(prospectStreamRef.current?.getVideoTracks()[0] || null);
  }, [screenSharingActive]);

  const onToggleAudio = useCallback(async () => {
    if (prospectStreamRef.current?.getAudioTracks()[0]) {
      if (prospectStreamRef.current.getAudioTracks()[0].readyState === 'live') {
        prospectStreamRef.current.getAudioTracks()[0].stop();
        setAudioActive(false);
      } else {
        try {
          const stream = await navigator.mediaDevices.getUserMedia({ ...mediaConstraints, video: false });
          prospectStreamRef.current.removeTrack(prospectStreamRef.current.getAudioTracks()[0]);
          prospectStreamRef.current.addTrack(stream.getAudioTracks()[0]);
          if (prospectVideo.current) prospectVideo.current.srcObject = prospectStreamRef.current;
          setAudioActive(true);
        } catch (e) {
          console.error('Error toggling audio', e);
        }
      }
      peerRef.current?.getSenders()?.find(s => s.track?.kind === 'audio')?.replaceTrack(prospectStreamRef.current?.getAudioTracks()[0] || null);
    }
  }, []);

  const restoreCall = useCallback(async () => {
    setOpenPermissionRequestsDialog(false);
    if (connectionState !== 'connected' && offer) handleReceiveCall(JSON.parse(offer));
  }, [handleReceiveCall, offer, connectionState]);

  useEffect(() => {
    const checkPerms = async () => {
      try {
        const perms = await Promise.all(['camera', 'microphone'].map(n => navigator.permissions.query({ name: n as any })));
        perms.forEach(p => p.onchange = () => { if (p.state === 'denied') setOpenPermissionRequestsDialog(true); });
      } catch (e) {
        console.error('Error checking permissions', e);
      }
    };
    checkPerms();
  }, []);

  useEffect(() => {
    if (channel.current?.readyState === 'open') {
      try {
        channel.current.send(JSON.stringify({ prospectVideoActive }));
      } catch (e) {
        console.error('Error sending video status message', e);
      }
    }
  }, [prospectVideoActive]);

  const openWait = useMemo(() => {
    if (isCallClosedByCf) return true;
    return (status !== 'CONNECTED' && connectionState !== 'connected') || (!!connectionState && connectionState !== 'connected' && connectionState !== 'closed' && !isPlaying);
  }, [status, connectionState, isPlaying, isCallClosedByCf]);

  const showProspectVideo = useMemo(() => isCallClosedByCf || (status === 'CONNECTED' && connectionState === 'connected'), [status, connectionState, isCallClosedByCf]);

  return (
    <><div className='web-collaboration-overlay'>
      <div className={`web-collaboration-overlay-buttons ${status !== 'CONNECTED' || connectionState !== 'connected' || openReconnectingDialog || isCallClosedByCf ? 'invisible' : ''}`} style={{ zIndex: (!openWait && !openPermissionRequestsDialog && !screenSharingActive && !(openReconnectingDialog && connectionState !== 'connected')) || openReconnectingDialog ? 10000 : 100 }} ref={buttonsRef}>
        <AppButtonIcon icon='close-call' className='web-collaboration-overlay-icon-button' label={formatMessage(webCollaborationMessages.callButton)} onClick={handleCloseCall} />
        <AppButtonIcon icon={audioActive ? 'microphone-on' : 'microphone-off'} className='web-collaboration-overlay-icon-button' label={formatMessage(webCollaborationMessages.microphoneButton)} onClick={onToggleAudio} />
        <AppButtonIcon icon={prospectVideoActive ? 'webcam-on' : 'webcam-off'} className='web-collaboration-overlay-icon-button' label={formatMessage(webCollaborationMessages.webcamButton)} onClick={onToggleVideo} />
        <AppButtonIcon icon={screenSharingActive ? 'share-video-on' : 'share-video-off'} className='web-collaboration-overlay-icon-button' label={formatMessage(webCollaborationMessages.sharedVideoButton)} onClick={screenSharingActive ? onStopSharedScreen : onShareScreen} />
      </div>
      {isCallClosedByCf && (
        <div className='web-collaboration-overlay-buttons' style={{ zIndex: 10000 }}>
          <AppButtonIcon icon='close-call' className='web-collaboration-overlay-icon-button' label={formatMessage(webCollaborationMessages.callButton)} onClick={handleCloseCall} />
        </div>
      )}
      <div className='web-collaboration-overlay-videos' style={{ zIndex: openReconnectingDialog || openWait || (!openPermissionRequestsDialog && !screenSharingActive && !(connectionState === 'connected')) ? 10000 : 100 }}>
        <Draggable nodeRef={prospectDraggableRef}><div ref={prospectDraggableRef} className={`web-collaboration-overlay-video ${!showProspectVideo ? 'invisible' : ''}`}><video className='web-collaboration-overlay-prospect-video' autoPlay ref={prospectVideo} muted /></div></Draggable>
        <Draggable nodeRef={cfDraggableRef}><div ref={cfDraggableRef} className={`web-collaboration-overlay-video ${status !== 'CONNECTED' || connectionState !== 'connected' || isCallClosedByCf ? 'invisible' : ''}`}><video className='web-collaboration-overlay-cf-video' autoPlay ref={cfVideo} /></div></Draggable>
      </div>
    </div>
    <AppDialog open={openWait || openReconnectingDialog} className='web-collaboration-overlay-cf-dialog'>
      <div className='wait-for-cf-dialog'><div className='wait-for-cf-dialog-section'><p className='wait-for-cf-dialog-content'>{formatMessage(!openReconnectingDialog && status === 'CONNECTED' && !isCallClosedByCf ? webCollaborationMessages.connecting : webCollaborationMessages.waitForCf)}</p></div><div className='wait-for-cf-dialog-button-section'><AppButtonIcon icon='close-call' className='web-collaboration-overlay-icon-button' label={formatMessage(webCollaborationMessages.closeCallButton)} onClick={handleCloseCall} /></div></div>
    </AppDialog>
    <WebCollaborationPermissionRequestsDialog open={openPermissionRequestsDialog} onClose={handleCloseCall} onContinue={restoreCall} />
    <WebCollaborationSharingBlockDialog open={!openWait && !openPermissionRequestsDialog && !screenSharingActive && !(openReconnectingDialog && connectionState !== 'connected')} onSharing={onShareScreen} /></>
  );
};